import { isNumber } from '../number'

type X = Record<string, any>
type Z = number | string | boolean | undefined | null
type ZMap = Record<string, Z>[]

/** return the union of all keys in provided objects */
export const getAllKeys = (obs: ZMap) =>
  obs.reduce((a, ob) => {
    Object.keys(ob).forEach((k) => {
      if (!a.includes(k)) a.push(k)
    })
    return a
  }, [] as string[])

export const objectsKeysAsRow = (obs: Record<string, Z>[]): Z[][] => getAllKeys(obs).map((k) => [k, ...obs.map((ob) => ob[k] ?? undefined)])

/** create array with first row as key definition row and fill with rows of the corresponing values for each object in input arrayy */
export const objectsKeysAsCol = (obs: Record<string, Z>[]): Z[][] => {
  const allKeys = getAllKeys(obs)
  return [allKeys, ...obs.map((ob) => allKeys.map((k) => ob[k] ?? undefined))]
}

/** assuming the first row is all string, treat as key definition row, return objects based on these keys */
export const keysAsColsToObjects = <T extends X = X>(arr: any[][]): T[] => {
  const xs: T[] = []
  if (arr[0].every((k) => typeof k === 'string')) {
    for (let row = 1; row < arr.length; row++) {
      xs.push(Object.fromEntries(arr[row].map((xv, col) => [arr[0][col], xv || xv === null || isNumber(xv) ? xv : undefined])))
    }
  }
  return xs
}

export const transposeValues = (values: (string | undefined | number | null | boolean)[][]) => {
  const transposed: (string | undefined | number | null | boolean)[][] = []
  values.forEach((row, i) => {
    if (i === 0) {
      row.forEach((col) => {
        transposed.push([col])
      })
    } else {
      row.forEach((col, j) => col && transposed[j]?.push(col))
    }
  })
  return transposed
}

export const keysAsRowsToObjects = (arr: any[][]): X[] => {
  const transposed = transposeValues(arr)
  return keysAsColsToObjects(transposed)
}

/** use to get indentation */
export const getArrayDims = (arr: any[][]) => {
  return { rows: arr.length, cols: arr.reduce((a, c) => (c.length > a ? c.length : a), 0) }
}
